import React, { useContext } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router";
import { Link } from "react-router-dom";
import { AuthContext } from "context";
import { useNavigate } from "react-router";
import View from "./PersonnalData/View";
import Edit from "./PersonnalData/Edit";
import NavTabs from "./NavTabs";
import MyProfil from "./MyProfil";
import Preferences from "./Preferences";
import Settings from "./Settings";
import Collaborators from "./Collaborator";
import MyServices from "./MyServices";
import MyAds from "./MyAds";
import MyApplications from "./MyApplications";
import MyProviders from "./MyProviders";
import MyFavorites from "./MyFavorites";
import ServiceHistory from "./ServiceHistory";
import ImageUpload from "components/File/ImageUpload";
import { MapPin, Star, Sparkles } from "lucide-react";
import { useProviderProfileCompletion } from "./useProviderProfileCompletion";
import {
    formatRemainingStepsLabel,
    getProfileCompletionStrokeDasharray,
    PROFILE_COMPLETION_SECTION_ID,
} from "./profileCompletion";
import Protected from "components/Secured/Protected";
import { formatRatingAverage } from "helpers/formatRatingAverage";
import { ROUTES } from "routes";

const ProfileRouter: React.FC = () => {
    const { account, updateAccount } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const onClickHandleMode = () => {
        if (location.pathname.includes("edition")) {
            navigate(location.pathname.replace("edition", ""));
        } else {
            navigate(location.pathname + "edition");
        }
    };

    const onEditSubmitSuccess = () => {
        void updateAccount();
        navigate(location.pathname.replace("edition", ""));
    };

    const userFirstName = account?.user.first_name ?? "Lorem";
    const userLastName = account?.user.last_name ? account.user.last_name.charAt(0) : "L";
    const city = account?.user.city ?? account?.user.address ?? "";
    const totalMissions = account?.resources?.provider?.[0]?.rating?.count ?? 0;
    const ratingAvg = account?.resources?.provider?.[0]?.rating?.average;
    const profileCompletion = useProviderProfileCompletion();

    const profileCompletionCard = profileCompletion ? (
        <>
            <div className="relative flex h-16 w-16 items-center justify-center">
                <svg className="absolute inset-0 -rotate-90" viewBox="0 0 64 64" aria-hidden>
                    <circle cx="32" cy="32" r="28" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                    <circle
                        cx="32"
                        cy="32"
                        r="28"
                        stroke="#4C4CF4"
                        strokeWidth="6"
                        fill="none"
                        strokeDasharray={getProfileCompletionStrokeDasharray(profileCompletion.percentage)}
                        strokeLinecap="round"
                    />
                </svg>
                <span className="text-sm font-bold text-gray-700">{profileCompletion.percentage}%</span>
            </div>
            <div>
                <div className="font-bold text-gray-900">Profil à {profileCompletion.percentage}%</div>
                <div className="text-xs font-medium uppercase tracking-wider text-primary">
                    {formatRemainingStepsLabel(profileCompletion.remainingSteps)}
                </div>
                {!profileCompletion.allComplete && (
                    <span className="mt-1 block text-sm font-semibold text-primary">Compléter mon profil</span>
                )}
            </div>
        </>
    ) : null;

    const profileCompletionCardClassName =
        "flex items-center gap-4 rounded-2xl bg-white px-5 py-4 shadow-sm";

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* En-tête profil */}
                <div className="flex flex-wrap items-start justify-between gap-6 mb-6">
                    <div className="flex items-center gap-5">
                        <div className="relative h-28 w-28 shrink-0">
                            <ImageUpload id={account?.user.picture_id} variant="avatar" />
                        </div>
                        <div>
                            <h1 className="mb-1 text-3xl font-extrabold tracking-tight text-gray-900">
                                {userFirstName} {userLastName}.
                            </h1>
                            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                                {city && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <MapPin className="h-4 w-4 text-slate-400" strokeWidth={2} />
                                        {city}
                                    </span>
                                )}
                                <span className="text-slate-300">•</span>
                                {(ratingAvg && totalMissions) ? (
                                    <span className="inline-flex items-center gap-1.5">
                                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                                        <span className="font-semibold text-slate-700">
                                            {ratingAvg ? formatRatingAverage(ratingAvg) : "—"}
                                        </span>
                                        <span className="text-slate-500">
                                            ({totalMissions} missions)
                                        </span>
                                    </span>
                                ) : (
                                    <div className="inline-flex items-center gap-1 text-green-600 font-bold">
                                        <Sparkles className="h-4 w-4"/>
                                        {"Nouveau"}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {profileCompletion &&
                        (profileCompletion.allComplete ? (
                            <div className={profileCompletionCardClassName}>{profileCompletionCard}</div>
                        ) : (
                            <Link
                                to={`${ROUTES.ACCOUNT_PROFILE}#${PROFILE_COMPLETION_SECTION_ID}`}
                                className={`${profileCompletionCardClassName} transition-colors hover:bg-slate-50`}
                                onClick={() => {
                                    document
                                        .getElementById(PROFILE_COMPLETION_SECTION_ID)
                                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                            >
                                {profileCompletionCard}
                            </Link>
                        ))}
                </div>

                {/* Onglets */}
                <div className="mb-6 overflow-hidden rounded-2xl bg-white shadow-sm">
                    <NavTabs />
                </div>

                {/* Contenu */}
                <div className="our_services_view_container profile">
                    <Routes>
                        <Route index element={<Navigate to="profil/" replace />} />
                        <Route path="*" element={<View onClickHandleMode={onClickHandleMode} />} />
                        <Route
                            path="edition"
                            element={<Edit onClickHandleMode={onClickHandleMode} onEditSubmitSuccess={onEditSubmitSuccess} />}
                        />
                        <Route path="profil/*" element={<MyProfil />} />
                        <Route path="preferences/*" element={<Preferences />} />
                        <Route path="collaborateurs/*" element={<Collaborators />} />
                        <Route path="historique/*" element={<ServiceHistory />} />
                        <Route path="mes-services/*" element={<Protected capability="service.*" element={<MyServices />} />} />
                        <Route path="mes-candidatures/*" element={<Protected capability="our_ad.*" element={<MyApplications />} />} />
                        <Route path="mes-annonces/*" element={<Protected capability="ad.*" element={<MyAds />} />} />
                        <Route path="mes-prestataires/*" element={<MyProviders />} />
                        <Route path="mes-favoris/*" element={<Protected capability="our_ad.*" element={<MyFavorites />} />} />
                        <Route path="parametres/*" element={<Settings />} />
                    </Routes>
                </div>
            </div>
        </div>
    );
};

export default ProfileRouter;
